import React, { useEffect, useState } from "react";
import axios from "axios";
import { SoundOutlined, PauseOutlined } from "@ant-design/icons";
import { Button, Tooltip, Select, Space } from "antd";
import { useSelector } from "react-redux";
import { checkParentElementHasClassName, getAIServiceBaseURL, getFromLS, updateConfigHeaderForDemoUseStagingAI } from "lib/utils/helperMethods";
import { useTranslate } from "lib/MrTranslate/MrTranslate";
import { enabledFeaturesSelector } from "views/Auth/Login/selector";

const TextToSpeech = () => {

  const [audioBlob, setAudioBlob] = useState(null);
  const [audio, setAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedText, setSelectedText] = useState(null);
  const [selectedTTSLanguage, setSelectedTTSLanguage] = useState({
    language: "en-us",
    name: "en-US-ChristopherNeural"
  })

  const enabledFeatures = useSelector(enabledFeaturesSelector())

  const textToSpeechAPIEndpoint = `${getAIServiceBaseURL()}/tts/cognitiveservices/v1`;
  console.log("textto specch ", process.env);
  const token = getFromLS("token");
  const mrIntl = useTranslate();
  const multiLanguageDropdown = enabledFeatures.enable_multi_language_dropdown || false;

  useEffect(() => {
    document.addEventListener("selectionchange", handleSelectionChange);
    document.addEventListener("mousedown", handleMouseDown);

    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [audioBlob]);

  useEffect(() => {
    if (audioBlob && isPlaying) {
      playAudio();
    } else {
      pauseAudio();
    }
  }, [audioBlob, isPlaying]);

  const handleSelectionChange = () => {
    try {
      const selectedTextToSpeak = window.getSelection().toString().trim();
      if(selectedTextToSpeak) {
        setSelectedText(selectedTextToSpeak);
      }
    } catch (error) {
      console.error('Error occurred in handleSelectionChange:', error);
    }
  };

  const handleMouseDown = (e) => {
    // We want to avoid executing the code below when clicking on the TTS Button. 
    const isClassExist = checkParentElementHasClassName(e.target, "text-to-speech-wrapper");
    if (!isClassExist) {
      setAudioBlob(null);
      setAudio(null);
      if(audioBlob){
        setSelectedText(null)
      }
    }
  };

  const manageTextToSpeechPlayback = async () => {
    if (!audioBlob) {
      // Make API request if audioBlob is not available
      try {
        const text = selectedText.replace(/&nbsp;|<br>/g, ' ');
        const speechSynthesisMarkupLanguage = `<speak version='1.0' xml:lang='${selectedTTSLanguage.language}'>
                      <voice xml:lang='${selectedTTSLanguage.language}' xml:gender='Male' name='${selectedTTSLanguage.name}'>
                        ${text}
                      </voice>
                    </speak>`;
        let config = {
          method: "POST",
          url: textToSpeechAPIEndpoint,
          headers: {
            "Content-Type": "application/ssml+xml",
            "X-Microsoft-OutputFormat": "audio-16khz-32kbitrate-mono-mp3",
            Authorization: token,
          },
          data: speechSynthesisMarkupLanguage,
          responseType: "arraybuffer",
        };
        config = updateConfigHeaderForDemoUseStagingAI(config)
        const response = await axios.request(config);
        // Convert array buffer to stream and create a ReadableStream from the response data received from the Text-to-Speech API.
        // This stream allows for the efficient reading of the audio data in chunks.
        const arrayBufferStream = new ReadableStream({
          start(controller) {
            // Enqueue the response data (converted to Uint8Array) into the stream controller.
            controller.enqueue(new Uint8Array(response.data));
            controller.close();
          },
        });

        // Convert the ReadableStream containing audio data into a Blob.
        const audioData = await new Response(arrayBufferStream).blob();
        setIsPlaying(true)
        setAudioBlob(audioData);
      } catch (error) {
        console.log(error);
      }
    } else {
      setIsPlaying((prevIsPlaying) => !prevIsPlaying);
    }
  };

  const playAudio = () => {
    if (!audioBlob) {
      return;
    }
    if (!audio) {
      const newAudio = new Audio(URL.createObjectURL(audioBlob));
      newAudio.onended = () => {
        setIsPlaying(false);
      };
      newAudio.play();
      setAudio(newAudio);
    } else {
      audio.play();
    }
  };

  const pauseAudio = () => {
    if (audio) {
      audio.pause();
      setIsPlaying(false);
    }
  };

  const handleLanguageChange = (value) => {
    let lang;
    let name;
  
    switch (value) {
      case 'en-US':
        lang = value;
        name = 'en-US-ChristopherNeural';
        break;
      case 'fr-FR':
        lang = value;
        name = 'fr-FR-AlainNeural';
        break;
      case 'es-ES':
        lang = value;
        name = 'es-ES-ArnauNeural';
        break;
      // Add more cases for other languages if needed
  
      default:
        lang = 'en-US';
        name = 'en-US-ChristopherNeural';
    }
    setSelectedTTSLanguage({
      language: lang,
      name: name
    })
  };

  return <>
    <Space className="text-to-speech-wrapper">
      <Tooltip title={!selectedText ? mrIntl("TextToSpeech.select_text_then_click_to_read_aloud") : isPlaying ? mrIntl("CommonText.pause") : mrIntl("TextToSpeech.read_aloud")}>
        <Button disabled={!selectedText}  onClick={() => manageTextToSpeechPlayback()} icon={isPlaying ? <PauseOutlined /> : <SoundOutlined />}  />
      </Tooltip>
      {multiLanguageDropdown && <Select
        onChange={(val) => handleLanguageChange(val)}
        defaultValue="en-US"
        options={[
          {
            value: 'en-US',
            label: "English",
          },
          {
            value: 'fr-FR',
            label: "French",
          },
          {
            value: 'es-ES',
            label: "Spanish",
          },
        ]} />}
    </Space>
  </>;
};
export default TextToSpeech;